import React from 'react';
import { Box, Typography, Popover, Button, IconButton, Grid } from '@mui/material';
import { KeyboardArrowLeftIcon, KeyboardArrowRightIcon, CloseIcon } from 'icons/materialIcons';
import { format as formatDateFns, isSameDay } from 'date-fns';

const TeamCalendarPopover = ({
    open,
    anchorEl,
    onClose,
    title = 'Select Date',
    mode = 'single', // 'single' | 'range'
    calendarViewDate,
    onPrevMonth,
    onNextMonth,
    calendarDays = [],
    onDateSelect,
    selectedDate = null,
    tempDateRange = [null, null],
    predefinedRanges = [],
    onSelectPredefinedRange,
    onReset,
    onApply,
    formatMonthYear = (date) => formatDateFns(date, 'MMMM yyyy'),
    formatFullDate = (date) => formatDateFns(date, 'MMM dd, yyyy')
}) => {
    const isRangeMode = mode === 'range';

    const isInRange = (date, startDate, endDate) => {
        if (!date || !startDate || !endDate) return false;
        return date >= startDate && date <= endDate;
    };

    const selectedSingleDate = selectedDate ? new Date(selectedDate) : null;

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            disablePortal={false}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            sx={{ zIndex: 1300, mt: 0.5 }}
            PaperProps={{
                sx: {
                    width: isRangeMode ? 560 : 420,
                    borderRadius: '16px',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    padding: 0,
                    overflow: 'hidden',
                    position: 'relative',
                    zIndex: 1300
                }
            }}
        >
            <Box sx={{
                padding: '16px 20px',
                borderBottom: '1px solid #e0e0e0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
                    {title}
                </Typography>
                <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ display: 'flex' }}>
                {isRangeMode && (
                    <Box sx={{
                        width: '160px',
                        borderRight: '1px solid #e0e0e0',
                        padding: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                    }}>
                        <Typography variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 600, mb: 1 }}>
                            Preset Ranges
                        </Typography>
                        {predefinedRanges.map((preset, index) => (
                            <Button
                                key={index}
                                onClick={() => onSelectPredefinedRange?.(preset.range)}
                                variant="text"
                                size="small"
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    color: '#666',
                                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                                    padding: '4px 8px',
                                    minHeight: '32px',
                                    fontSize: '13px'
                                }}
                            >
                                {preset.label}
                            </Button>
                        ))}
                    </Box>
                )}

                <Box sx={{ flex: 1, padding: '0' }}>
                    <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 16px',
                        borderBottom: '1px solid #e0e0e0'
                    }}>
                        <IconButton size="small" onClick={onPrevMonth}>
                            <KeyboardArrowLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            {formatMonthYear(calendarViewDate)}
                        </Typography>
                        <IconButton size="small" onClick={onNextMonth}>
                            <KeyboardArrowRightIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box sx={{ padding: '16px' }}>
                        <Grid container>
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                <Grid item xs={12 / 7} key={index} sx={{ textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#666' }}>
                                        {day}
                                    </Typography>
                                </Grid>
                            ))}
                        </Grid>

                        <Grid container sx={{ mt: 1 }}>
                            {calendarDays.map((dayObj, index) => (
                                <Grid item xs={12 / 7} key={index}>
                                    {dayObj.day !== null && (
                                        <Box
                                            onClick={() => onDateSelect?.(dayObj.date)}
                                            sx={{
                                                height: '40px',
                                                width: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                borderRadius: '50%',
                                                margin: '4px auto',

                                                ...(isRangeMode && tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0]) && {
                                                    backgroundColor: '#000',
                                                    color: '#fff'
                                                }),
                                                ...(isRangeMode && tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1]) && {
                                                    backgroundColor: '#000',
                                                    color: '#fff'
                                                }),
                                                ...(isRangeMode && tempDateRange[0] && tempDateRange[1] && isInRange(dayObj.date, tempDateRange[0], tempDateRange[1]) && {
                                                    backgroundColor: 'rgba(0,0,0,0.1)'
                                                }),
                                                ...(!isRangeMode && selectedSingleDate && isSameDay(dayObj.date, selectedSingleDate) && {
                                                    backgroundColor: '#000',
                                                    color: '#fff'
                                                }),
                                                ...(isSameDay(dayObj.date, new Date()) &&
                                                    (isRangeMode
                                                        ? (!tempDateRange[0] || !isSameDay(dayObj.date, tempDateRange[0])) &&
                                                        (!tempDateRange[1] || !isSameDay(dayObj.date, tempDateRange[1]))
                                                        : (!selectedSingleDate || !isSameDay(dayObj.date, selectedSingleDate))) && {
                                                        border: '1px solid #000'
                                                    }),
                                                '&:hover': {
                                                    backgroundColor:
                                                        (isRangeMode && ((tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) || (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1])))) ||
                                                            (!isRangeMode && selectedSingleDate && isSameDay(dayObj.date, selectedSingleDate))
                                                            ? '#000'
                                                            : 'rgba(0,0,0,0.2)',
                                                    color:
                                                        (isRangeMode && ((tempDateRange[0] && isSameDay(dayObj.date, tempDateRange[0])) || (tempDateRange[1] && isSameDay(dayObj.date, tempDateRange[1])))) ||
                                                            (!isRangeMode && selectedSingleDate && isSameDay(dayObj.date, selectedSingleDate))
                                                            ? '#fff'
                                                            : 'inherit'
                                                }
                                            }}
                                        >
                                            <Typography sx={{ fontSize: '14px' }}>{dayObj.day}</Typography>
                                        </Box>
                                    )}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Box>

            {isRangeMode && (
                <Box sx={{
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                Start Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[0] ? formatFullDate(tempDateRange[0]) : 'Select date'}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: '#666', fontSize: '12px' }}>
                                End Date
                            </Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                                {tempDateRange[1] ? formatFullDate(tempDateRange[1]) : 'Select date'}
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                        <Button
                            onClick={onReset}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: '#e0e0e0',
                                color: '#666',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': {
                                    borderColor: '#c0c0c0',
                                    backgroundColor: 'rgba(0,0,0,0.02)'
                                }
                            }}
                        >
                            Reset to All Time
                        </Button>
                        <Button
                            onClick={onApply}
                            variant="contained"
                            size="small"
                            sx={{
                                backgroundColor: '#000',
                                color: '#fff',
                                textTransform: 'none',
                                fontSize: '13px',
                                borderRadius: '20px',
                                '&:hover': { backgroundColor: '#333' }
                            }}
                        >
                            Apply Filter
                        </Button>
                    </Box>
                </Box>
            )}
        </Popover>
    );
};

export default TeamCalendarPopover;
